import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Plus, Award, Edit2, Trash2, X, Building2 } from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import Popup from "components/minor/popup/popup";
import { useTheme } from "context/ThemeContext";

const Schema = Yup.object({ title: Yup.string().required("Title is required") });
const LEVELS = ["Intern","Junior","Mid","Senior","Lead","Manager","Director","VP","C-Suite"];

export default function DesignationManagement() {
  const { T } = useTheme();
  const LEVEL_COLORS = { Intern:T.muted, Junior:T.green, Mid:T.cyan, Senior:T.purple, Lead:T.amber, Manager:T.red, Director:T.red, VP:T.red, "C-Suite":T.red };

  const [designations, setDesignations] = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [open,     setOpen]     = useState(false);
  const [mode,     setMode]     = useState("add");
  const [sel,      setSel]      = useState(null);
  const [delOpen,  setDelOpen]  = useState(false);
  const [delId,    setDelId]    = useState(null);
  const [deptFilter,setDeptFilter]=useState("");

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [dRes, deRes] = await Promise.all([
        axios.get(APIConstant.departments),
        axios.get(APIConstant.designations + (deptFilter?`?department_id=${deptFilter}`:"")),
      ]);
      setDepartments(dRes.data||[]);
      setDesignations(deRes.data||[]);
    } catch(e){ console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(()=>{ fetchAll(); },[deptFilter]);

  const handleSubmit = async (vals) => {
    try {
      const payload = {...vals, department_id: vals.department_id ? parseInt(vals.department_id) : null};
      if (mode==="edit"&&sel) await axios.put(`${APIConstant.designations}${sel.id}`, payload);
      else await axios.post(APIConstant.designations, payload);
      setOpen(false); fetchAll();
    } catch(e){ console.error(e); }
  };

  const handleDelete = async () => {
    try { await axios.delete(`${APIConstant.designations}${delId}`); setDelOpen(false); fetchAll(); }
    catch(e){ console.error(e); }
  };

  // Group by department
  const grouped = {};
  designations.forEach(d => {
    const key = d.department_name || "General";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(d);
  });

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>EMPLOYEE MANAGEMENT</div>
          <h2>Designations</h2>
          <p>Job titles and levels across departments</p>
        </div>
        <div className={styles.headerRight}>
          <select value={deptFilter} onChange={e=>setDeptFilter(e.target.value)}
            style={{background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:13,padding:"9px 12px",outline:"none",fontFamily:"Outfit,sans-serif"}}>
            <option value="" style={{background:T.opt_bg}}>All Departments</option>
            {departments.map(d=><option key={d.id} value={d.id} style={{background:T.opt_bg}}>{d.name}</option>)}
          </select>
          <button className={styles.btnPrimary} onClick={()=>{setMode("add");setSel(null);setOpen(true);}}>
            <Plus size={14}/> Add Designation
          </button>
        </div>
      </div>

      {loading ? <div className={styles.loader}><div className={styles.spinner}/>Loading…</div> : (
        <div style={{display:"flex",flexDirection:"column",gap:20}}>
          {Object.entries(grouped).map(([dept, desigs])=>(
            <div key={dept} className={styles.card}>
              <div className={styles.cardHeader}>
                <div style={{display:"flex",alignItems:"center",gap:9}}>
                  <Building2 size={15} style={{color:T.cyan}}/>
                  <h3>{dept}</h3>
                </div>
                <span className={styles.cardMeta}>{desigs.length} designations</span>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.darkTable}>
                  <thead><tr><th>Title</th><th>Level</th><th>Department</th><th>Actions</th></tr></thead>
                  <tbody>
                    {desigs.map(d=>(
                      <tr key={d.id}>
                        <td style={{fontWeight:500}}>{d.title}</td>
                        <td>
                          <span className={`${styles.badge}`} style={{background:`${LEVEL_COLORS[d.level]||T.muted}20`,color:LEVEL_COLORS[d.level]||T.muted}}>
                            {d.level||"Mid"}
                          </span>
                        </td>
                        <td className={styles.dim}>{d.department_name||"—"}</td>
                        <td>
                          <div style={{display:"flex",gap:6}}>
                            <button className={styles.btnSm} onClick={()=>{setMode("edit");setSel(d);setOpen(true);}}><Edit2 size={12}/> Edit</button>
                            <button className={styles.btnSm} style={{color:T.red,borderColor:`${T.red}40`}}
                              onClick={()=>{setDelId(d.id);setDelOpen(true);}}><Trash2 size={12}/></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {designations.length===0&&<div style={{textAlign:"center",padding:"60px",color:T.muted}}>No designations yet.</div>}
        </div>
      )}

      {/* Form Modal */}
      <Dialog open={open} onClose={()=>setOpen(false)}>
        <div style={{background:T.card,border:`1px solid ${T.cyan}33`,borderRadius:14,width:460,fontFamily:"Outfit,sans-serif",overflow:"hidden"}}>
          <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,background:`${T.cyan}0A`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <h3 style={{margin:0,fontSize:16,fontWeight:600,color:T.text}}>{mode==="add"?"Add":"Edit"} Designation</h3>
            <button style={{background:"none",border:"none",cursor:"pointer",color:T.muted}} onClick={()=>setOpen(false)}><X size={16}/></button>
          </div>
          <div style={{padding:24}}>
            <Formik
              enableReinitialize
              initialValues={mode==="edit"&&sel
                ? {title:sel.title,level:sel.level||"Mid",department_id:sel.department_id||""}
                : {title:"",level:"Mid",department_id:""}}
              validationSchema={Schema}
              onSubmit={handleSubmit}
            >
              {({errors,touched})=>(
                <Form>
                  <div style={{marginBottom:16}}>
                    <label style={{fontSize:11.5,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>Job Title *</label>
                    <Field name="title" placeholder="e.g. Senior Software Engineer"
                      style={{width:"100%",background:T.inp_bg,border:`1px solid ${errors.title&&touched.title?T.red:T.border}`,borderRadius:8,color:T.text,fontSize:13,padding:"9px 12px",outline:"none",fontFamily:"Outfit,sans-serif",boxSizing:"border-box"}}/>
                    {errors.title&&touched.title&&<p style={{color:T.red,fontSize:11.5,marginTop:4}}>{errors.title}</p>}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:18}}>
                    <div>
                      <label style={{fontSize:11.5,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>Level</label>
                      <Field as="select" name="level"
                        style={{width:"100%",background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:13,padding:"9px 12px",outline:"none",fontFamily:"Outfit,sans-serif"}}>
                        {LEVELS.map(l=><option key={l} style={{background:T.opt_bg}}>{l}</option>)}
                      </Field>
                    </div>
                    <div>
                      <label style={{fontSize:11.5,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>Department</label>
                      <Field as="select" name="department_id"
                        style={{width:"100%",background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:13,padding:"9px 12px",outline:"none",fontFamily:"Outfit,sans-serif"}}>
                        <option value="" style={{background:T.opt_bg}}>Select</option>
                        {departments.map(d=><option key={d.id} value={d.id} style={{background:T.opt_bg}}>{d.name}</option>)}
                      </Field>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:12,borderTop:`1px solid ${T.border}`}}>
                    <button type="button" onClick={()=>setOpen(false)} className={styles.btnSecondary}>Cancel</button>
                    <button type="submit" className={styles.btnPrimary}>{mode==="add"?"Save":"Update"}</button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Dialog>

      <Popup open={delOpen} message="Delete this designation?" handleClose={()=>setDelOpen(false)} handleConfirm={handleDelete}/>
    </div>
  );
}
